import React, { useState, useEffect } from 'react';
import { Tent, User, Calendar, MapPin, Activity, ChevronLeft, ChevronRight } from 'lucide-react';

const AuditLog = () => {
    const [auditData, setAuditData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        total: 0,
        limit: 25,
        offset: 0,
        has_more: false
    });
    const [filters, setFilters] = useState({
        operation_type: '',
        camp_location_id: '',
        user_id: ''
    });

    const fetchAuditData = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                limit: pagination.limit,
                offset: pagination.offset,
                ...Object.fromEntries(
                    Object.entries(filters).filter(([_, value]) => value !== '')
                )
            });

            const response = await fetch(`/otgservices/getAuditLog.php?${params}`);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            
            if (data.success) {
                setAuditData(data.data);
                setPagination(data.pagination);
                setError(null);
            } else {
                throw new Error(data.error || 'Failed to fetch audit data');
            }
        } catch (err) {
            console.error('Error fetching audit data:', err);
            setError(err.message);
            setAuditData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAuditData();
    }, [pagination.offset, pagination.limit]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
        setPagination(prev => ({ ...prev, offset: 0 })); // Reset to first page
    };

    const applyFilters = () => {
        setPagination(prev => ({ ...prev, offset: 0 }));
        fetchAuditData();
    };

    const clearFilters = () => {
        setFilters({
            operation_type: '',
            camp_location_id: '',
            user_id: ''
        });
        setPagination(prev => ({ ...prev, offset: 0 }));
    };

    const handlePageChange = (direction) => {
        setPagination(prev => ({
            ...prev,
            offset: direction === 'next' 
                ? prev.offset + prev.limit 
                : Math.max(0, prev.offset - prev.limit)
        }));
    };

    const formatDateTime = (dateTimeString) => {
        return new Date(dateTimeString).toLocaleString();
    };

    const getOperationIcon = (operation) => {
        switch (operation) {
            case 'ADD':
                return <Activity className="w-4 h-4 text-green-600" />;
            case 'UPDATE':
                return <Activity className="w-4 h-4 text-blue-600" />;
            case 'DELETE':
                return <Activity className="w-4 h-4 text-red-600" />;
            default:
                return <Activity className="w-4 h-4 text-gray-600" />;
        }
    };

    const getOperationColor = (operation) => {
        switch (operation) {
            case 'ADD':
                return 'bg-green-100 text-green-800';
            case 'UPDATE':
                return 'bg-blue-100 text-blue-800';
            case 'DELETE':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-orange-600 text-white px-6 py-4">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="bg-white p-2 rounded">
                            <Tent className="w-6 h-6 text-orange-600" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold">OTG Camp</h1>
                            <p className="text-sm opacity-90">AUDIT LOG</p>
                        </div>
                    </div>
                    <div className="text-right">
                        <span className="text-sm bg-orange-700 px-2 py-1 rounded">Pro</span>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto p-6">
                {/* Filters */}
                <div className="bg-white rounded-lg shadow p-6 mb-6">
                    <h2 className="text-lg font-semibold mb-4">Filters</h2>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Operation Type
                            </label>
                            <select
                                value={filters.operation_type}
                                onChange={(e) => handleFilterChange('operation_type', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            >
                                <option value="">All Operations</option>
                                <option value="ADD">Add</option>
                                <option value="UPDATE">Update</option>
                                <option value="DELETE">Delete</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Camp Location ID
                            </label>
                            <input
                                type="number"
                                value={filters.camp_location_id}
                                onChange={(e) => handleFilterChange('camp_location_id', e.target.value)}
                                placeholder="Enter camp ID"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                User ID
                            </label>
                            <input
                                type="number"
                                value={filters.user_id}
                                onChange={(e) => handleFilterChange('user_id', e.target.value)}
                                placeholder="Enter user ID"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            />
                        </div>
                        <div className="flex items-end gap-2">
                            <button
                                onClick={applyFilters}
                                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
                            >
                                Apply
                            </button>
                            <button
                                onClick={clearFilters}
                                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="bg-white rounded-lg shadow">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-xl font-semibold text-gray-800">
                            Audit Log
                            {pagination.total > 0 && (
                                <span className="text-sm text-gray-500 ml-2">
                                    ({pagination.total} total entries)
                                </span>
                            )}
                        </h2>
                    </div>

                    {loading && (
                        <div className="flex justify-center items-center h-48">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
                        </div>
                    )}

                    {error && (
                        <div className="p-6 text-center text-red-600">
                            <p>Error: {error}</p>
                            <button
                                onClick={fetchAuditData}
                                className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                            >
                                Retry
                            </button>
                        </div>
                    )}

                    {!loading && !error && auditData.length === 0 && (
                        <div className="p-6 text-center text-gray-500">
                            <p>No audit entries found.</p>
                        </div>
                    )}

                    {!loading && !error && auditData.length > 0 && (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            <div className="flex items-center gap-1">
                                                <Calendar className="w-4 h-4" />
                                                Date/Time
                                            </div>
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Operation
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            <div className="flex items-center gap-1">
                                                <MapPin className="w-4 h-4" />
                                                Camp
                                            </div>
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            <div className="flex items-center gap-1">
                                                <User className="w-4 h-4" />
                                                User
                                            </div>
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Details
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            IP Address
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {auditData.map((entry) => (
                                        <tr key={entry.audit_id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {formatDateTime(entry.created_at)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    {getOperationIcon(entry.operation_type)}
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getOperationColor(entry.operation_type)}`}>
                                                        {entry.operation_type}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                <div>
                                                    <div className="font-medium">{entry.camp_name}</div>
                                                    <div className="text-gray-500">
                                                        ID: {entry.camp_location_id}
                                                        {entry.camp_type && ` • ${entry.camp_type}`}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {entry.user_display}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-900">
                                                <div className="max-w-md break-words whitespace-normal">
                                                    {entry.details || 'No details'}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {entry.ip_address || 'N/A'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Pagination */}
                    {!loading && !error && pagination.total > 0 && (
                        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                            <div className="text-sm text-gray-500">
                                Showing {pagination.offset + 1} to {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} entries
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => handlePageChange('prev')}
                                    disabled={pagination.offset === 0}
                                    className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm ${
                                        pagination.offset === 0
                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                    }`}
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                    Previous
                                </button>
                                <button
                                    onClick={() => handlePageChange('next')}
                                    disabled={!pagination.has_more}
                                    className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm ${
                                        !pagination.has_more
                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                    }`}
                                >
                                    Next
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AuditLog;